import { client } from '@/client';
import {
  AccountVariable,
  AccountVariableType,
  SaveAccountVariableRequest,
} from '@/client/accounts';
import { GenericForbiddenApiError } from '@/hooks/query/types';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

const queryKey = 'account-variables';

export const useAccountVariables = (
  accountId?: string,
  params?: UseQueryOptions,
): UseQueryResult<AccountVariable[]> & {
  variables: AccountVariable[] | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params || {};

  const { data, ...rest } = useQuery(
    [queryKey, accountId],
    () => client.accountVariables.getAccountVariables(accountId as string),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      enabled: !!accountId,
      ...(restOptions as any),
    },
  );

  return {
    variables: data,
    ...(rest as UseQueryResult<AccountVariable[]>),
  };
};

export const useSaveAccountVariable = (
  accountId: string,
): {
  saveVariable: (input: {
    type: AccountVariableType;
    payload: SaveAccountVariableRequest;
  }) => Promise<AccountVariable>;
  isLoading: boolean;
} => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation<
    AccountVariable,
    GenericForbiddenApiError,
    { type: AccountVariableType; payload: SaveAccountVariableRequest }
  >(
    'saveAccountVariable',
    ({ type, payload }) =>
      client.accountVariables.saveAccountVariable(accountId, type, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey, accountId]);
      },
    },
  );

  return {
    saveVariable: mutateAsync,
    ...(rest as any),
  };
};
